import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import "./country-gateway.css";
import {
  COUNTRY_LANGUAGE_ROUTES,
  COUNTRY_OPTIONS,
  TRAVELSOMO_REGISTER_URL,
  getTravelsomoRedirectCopy,
} from "./countryLanguageRoutes";

const TRAVELSOMO_REDIRECT_DELAY_MS = 2200;

const FLAG_ASSET_CODES = {
  IL: "il",
  US: "us",
  GB: "gb",
  GR: "gr",
  FR: "fr",
  ES: "es",
  IT: "it",
  DE: "de",
};

export default function WelcomePage() {
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  const [travelsomoNotice, setTravelsomoNotice] = useState(null);
  const locale =
    typeof document !== "undefined"
      ? document.documentElement.lang || navigator.language || "unknown"
      : "unknown";

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const trace = window.__HR_ORIGINAL_CONSOLE_INFO__ || console.info;
    trace("[GATEWAY_UI_RENDER_TRACE]", {
      layoutVersion: "V4",
      countryCount: COUNTRY_OPTIONS.length,
      locale,
      cardsRendered: COUNTRY_OPTIONS.length,
    });
  }, [locale]);

  const handleCountrySelect = (countryCode) => {
    if (selectedCountryCode) {
      return;
    }

    const option = COUNTRY_OPTIONS.find((item) => item.code === countryCode);
    const targetRoute = COUNTRY_LANGUAGE_ROUTES[countryCode];
    const redirectToTravelsomo = countryCode !== "IL";

    if (!redirectToTravelsomo) {
      localStorage.setItem("hotelsriver_country", countryCode);
      localStorage.setItem("hotelsriver_language_route", targetRoute);
    }

    if (import.meta.env.DEV) {
      const trace = window.__HR_ORIGINAL_CONSOLE_INFO__ || console.info;
      trace("[GATEWAY_COUNTRY_SELECT_TRACE]", {
        countryCode,
        targetRoute: redirectToTravelsomo ? TRAVELSOMO_REGISTER_URL : targetRoute,
        timestamp: new Date().toISOString(),
      });
    }

    setSelectedCountryCode(countryCode);

    if (redirectToTravelsomo) {
      setTravelsomoNotice(getTravelsomoRedirectCopy(option?.language));
      redirectTimerRef.current = window.setTimeout(() => {
        window.location.assign(TRAVELSOMO_REGISTER_URL);
      }, TRAVELSOMO_REDIRECT_DELAY_MS);
      return;
    }

    redirectTimerRef.current = window.setTimeout(() => {
      navigate(targetRoute);
    }, 300);
  };

  return (
    <main
      className={`country-gateway${selectedCountryCode ? " country-gateway--selecting" : ""}`}
      aria-labelledby="country-gateway-title"
    >
      <div className="country-gateway__fallback" aria-hidden="true" />
      <video
        className="country-gateway__video"
        src="/videos/hotelsriver-gateway-bg.mp4"
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      />
      <div className="country-gateway__overlay" aria-hidden="true" />
      <div className="country-gateway__grid" aria-hidden="true" />
      <div className="country-gateway__map" aria-hidden="true" />
      <div className="country-gateway__flight-path" aria-hidden="true" />
      <div className="country-gateway__light-trail country-gateway__light-trail--orange" aria-hidden="true" />
      <div className="country-gateway__light-trail country-gateway__light-trail--blue" aria-hidden="true" />
      <div className="country-gateway__destinations" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>

      <section className="country-gateway__hero">
        <div className="country-gateway__hero-inner">
          <img
            className="country-gateway__logo"
            src={HOTELSRIVER_OFFICIAL_LOGO_URL}
            alt="HotelsRiver"
          />
          <p className="country-gateway__brand-name">HotelsRiver</p>
          <p className="country-gateway__kicker">B2B &amp; B2C PLATFORM FOR HOTELS</p>
          <h1 id="country-gateway-title">Welcome to HotelsRiver</h1>
          <p className="country-gateway__subtitle">Tell us about your location</p>
          <p className="country-gateway__hero-copy">
            HotelsRiver is a global B2B &amp; B2C platform for hotels. Select your region and
            we'll tailor your experience.
          </p>
          <p className="country-gateway__cta-hint">↓ Select your region to continue</p>
        </div>
      </section>

      <aside className="country-gateway__panel" aria-label="Choose your location">
        <div className="country-gateway__panel-header">
          <h2>{travelsomoNotice ? travelsomoNotice.title : "Choose your location"}</h2>
          <p>
            {travelsomoNotice
              ? travelsomoNotice.body
              : "Recommended language and regional experience"}
          </p>
        </div>

        {travelsomoNotice ? (
          <div className="country-gateway__travelsomo" role="status" aria-live="polite">
            <a className="country-gateway__travelsomo-cta" href={TRAVELSOMO_REGISTER_URL}>
              {travelsomoNotice.cta}
            </a>
            <p className="country-gateway__travelsomo-hint">{travelsomoNotice.hint}</p>
          </div>
        ) : (
        <div className="country-gateway__country-grid">
          {COUNTRY_OPTIONS.map((option) => (
            <button
              key={option.code}
              type="button"
              className={`country-gateway__country${
                selectedCountryCode === option.code ? " country-gateway__country--selected" : ""
              }`}
              onClick={() => handleCountrySelect(option.code)}
              aria-label={`Continue from ${option.country} in ${option.language}`}
              disabled={Boolean(selectedCountryCode)}
            >
              <span className="country-gateway__flag" aria-hidden="true">
                <img
                  src={`https://flagcdn.com/w80/${FLAG_ASSET_CODES[option.code]}.png`}
                  srcSet={`https://flagcdn.com/w80/${FLAG_ASSET_CODES[option.code]}.png 1x, https://flagcdn.com/w160/${FLAG_ASSET_CODES[option.code]}.png 2x`}
                  alt=""
                  loading="lazy"
                />
              </span>
              <span className="country-gateway__country-copy">
                <strong>{option.country}</strong>
                <small>Continue in {option.language}</small>
              </span>
              <span className="country-gateway__chevron" aria-hidden="true">
                →
              </span>
            </button>
          ))}
        </div>
        )}
      </aside>
    </main>
  );
}
